"use client";
//import node module libraries
import toast from "react-hot-toast";
import { v4 as uuid } from "uuid";
import { SubmitHandler, useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { useSearchParams } from "next/navigation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

//import custom shadcn UI components
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import CustomInput from "@/components/ui/custom-input";

//import server actions
import { updateDashboard } from "@/lib/api-client";

//import custom hooks
import { useDashboard } from "@/hooks";
import { dashItemProps } from "@/types";

type FormValues = {
  title: string;
  description: string;
};

const AddNewDashboard = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [selectedDashboard, setSelectedDashboard] =
    useState<dashItemProps | null>(null);

  const searchParams = useSearchParams();

  const dashboardId = searchParams.get("dashboardId");

  // const dashboard = useSelector((state: RootState) =>
  //   state.dashboard.dashboards.find((item) => item.dash_id === dashboardId)
  // );

  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    setError,
  } = useForm<FormValues>({
    defaultValues: {
      title: selectedDashboard?.dash_name ?? "",
      description: selectedDashboard?.dash_desc ?? "",
    },
  });

  const { dashboards, createDashboard } = useDashboard();

  useEffect(() => {
    const createMode = searchParams.get("create-dashboard") === "true";
    const updateMode = searchParams.get("update-dashboard") === "true";

    setIsCreateMode(createMode);
    setIsUpdateMode(updateMode);
    setIsOpen(createMode || updateMode);

    // }
  }, [searchParams]);

  useEffect(() => {
    if (dashboardId) {
      const selectedDashboard = (dashboards ?? []).find(
        (item: dashItemProps) => item.dash_id === dashboardId
      );
      setSelectedDashboard(selectedDashboard);
      setValue("title", selectedDashboard.dash_name);
      setValue("description", selectedDashboard.dash_desc);
    }
  }, [dashboardId, dashboards, setValue]);

  //update dashboard title and desc
  const { mutate: updateExistingDashboard, isPending } = useMutation({
    mutationFn: async (data: any) => await updateDashboard(data),
    onSuccess: async () => {
      toast.success("Dashboard Updated Successfully", {
        position: "bottom-right",
      });
      await queryClient.invalidateQueries({
        queryKey: ["dashboards"],
      });
      closeDialog();
    },
    onError: (context: any) => {
      toast.error("Dashboard update failed", {
        position: "bottom-right",
      });
      queryClient.setQueryData(["dashboards"], context.previousData);
    },
    onMutate: async (newData) => {
      const previousData = queryClient.getQueryData(["dashboards"]);

      return { previousData };
    },
  });

  const closeDialog = () => {
    if (isCreateMode) {
      const url = new URL(window.location.href);
      url.searchParams.delete("create-dashboard");
      window.history.pushState({}, "", url.toString());
    }

    if (isUpdateMode) {
      const url = new URL(window.location.href);
      url.searchParams.delete("update-dashboard");
      window.history.pushState({}, "", url.toString());
    }

    if (dashboardId) {
      const url = new URL(window.location.href);
      url.searchParams.delete("dashboardId");
      window.history.pushState({}, "", url.toString());
    }

    setIsCreateMode(false);
    setIsUpdateMode(false);
    setIsOpen(false);
    reset();
  };

  const submitHandler: SubmitHandler<FormValues> = async (data) => {
    const { title, description } = data;

    if (!title.trim()) {
      setError("title", {
        type: "manual",
        message: "Title is required",
      });
    }

    if (!description.trim()) {
      setError("description", {
        type: "manual",
        message: "Description is required",
      });
    }

    if (!title.trim() || !description.trim()) {
      toast.error("Please enter valid title and description", {
        position: "bottom-right",
      });
      return;
    }

    if (isUpdateMode) {
      const payload = {
        ...selectedDashboard,
        dash_name: data.title,
        dash_desc: data.description,
      };
      updateExistingDashboard(payload);
    } else if (isCreateMode) {
      const payload = {
        dashboard_id: uuid(),
        dashboard_name: data.title,
        dashboard_desc: data.description,
      };
      createDashboard(payload);
      closeDialog();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={closeDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isUpdateMode ? "Update Dashboard" : "Add New Dashboard"}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription>
          <span className="sr-only">Create and Update Dashboard</span>
        </DialogDescription>
        <form onSubmit={handleSubmit(submitHandler)}>
          <div className="flex flex-col gap-6">
            <CustomInput
              label="Dashboard Title"
              id="title"
              name="title"
              register={register}
              required
              errors={errors}
              errorMsg="Please enter a title"
            />

            <CustomInput
              label="Description"
              id="description"
              name="description"
              register={register}
              required
              errors={errors}
              errorMsg="Please enter description"
              as="textarea"
            />
          </div>
          <DialogFooter>
            <div className="flex items-center gap-4 pt-4">
              <Button
                variant={"outline"}
                onClick={() => closeDialog()}
                type="button"
              >
                Cancel
              </Button>
              <Button className="text-white" type="submit">
                {isUpdateMode
                  ? isPending
                    ? "Dashboard Updating..."
                    : "Update Dashboard"
                  : "Add New Dashboard"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddNewDashboard;
